import { h } from 'preact';
import ItemGoals from '../components/shared/GoalChips.jsx';
import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { t, translateError } from '../i18n.js';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import { parseServerDate } from '../utils/dates.js';
import { memberLabel, AwayNote } from '../utils/away.js';
import AiAssist from '../components/shared/AiAssist.jsx';
import { openFile } from '../utils/openFile.js';
import { PdfFrame } from '../components/shared/PdfPages.jsx';

// ── Task Collaborators ────────────────────────────────────────────────────────

function TaskCollaborators({ taskId, canManage, currentUserId, members }) {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingId, setAddingId] = useState('');
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    api(`/api/tasks/${taskId}/collaborators`)
      .then(data => setCollaborators(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [taskId]);

  async function handleAdd() {
    if (!addingId) return;
    setSaving(true);
    try {
      const updated = await api(`/api/tasks/${taskId}/collaborators`, {
        method: 'POST',
        body: JSON.stringify({ user_id: parseInt(addingId) }),
      });
      setCollaborators(Array.isArray(updated) ? updated : []);
      setAddingId('');
    } catch {
      // silently ignore duplicate
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(userId) {
    setRemovingId(userId);
    try {
      const updated = await api(`/api/tasks/${taskId}/collaborators/${userId}`, { method: 'DELETE' });
      setCollaborators(Array.isArray(updated) ? updated : []);
    } catch {
    } finally {
      setRemovingId(null);
    }
  }

  const collabIds = new Set(collaborators.map(c => c.user_id));
  const available = (members || []).filter(m => !collabIds.has(m.id) && m.id !== currentUserId);

  return (
    <Card title={`🤝 Samenwerkers${collaborators.length > 0 ? ` (${collaborators.length})` : ''}`}>
      {loading && <p style="color:var(--color-text-muted);font-size:13px">Laden...</p>}
      {!loading && collaborators.length === 0 && (
        <p style="color:var(--color-text-muted);font-size:13px">Nog geen samenwerkers toegevoegd.</p>
      )}
      {collaborators.length > 0 && (
        <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:12px">
          {collaborators.map(c => (
            <div key={c.user_id} style="display:flex;align-items:center;justify-content:space-between;padding:8px 10px;background:var(--color-bg-subtle);border-radius:6px">
              <div>
                <span style="font-weight:500;font-size:13px">{c.display_name}</span>
                {c.added_by_name && (
                  <span style="font-size:11px;color:var(--color-text-muted);margin-left:8px">toegevoegd door {c.added_by_name}</span>
                )}
              </div>
              {canManage && (
                <button
                  onClick={() => handleRemove(c.user_id)}
                  disabled={removingId === c.user_id}
                  style="background:none;border:none;cursor:pointer;color:var(--color-text-muted);font-size:16px;padding:2px 6px;border-radius:4px"
                  title="Verwijderen"
                >×</button>
              )}
            </div>
          ))}
        </div>
      )}
      {canManage && available.length > 0 && (
        <div style="display:flex;gap:8px;align-items:center">
          <select
            class="form-select"
            style="flex:1;font-size:13px"
            value={addingId}
            onChange={e => setAddingId(e.target.value)}
          >
            <option value="">— Gebruiker toevoegen —</option>
            {available.map(m => (
              <option key={m.id} value={m.id}>{m.display_name}</option>
            ))}
          </select>
          <Button onClick={handleAdd} disabled={!addingId || saving} size="sm">
            {saving ? '...' : 'Toevoegen'}
          </Button>
        </div>
      )}
      {canManage && available.length === 0 && collaborators.length > 0 && (
        <p style="font-size:12px;color:var(--color-text-muted)">Alle clubleden zijn al toegevoegd.</p>
      )}
    </Card>
  );
}

// ── Task Comments ─────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  const diff = Date.now() - parseServerDate(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'zojuist';
  if (mins < 60) return `${mins}m geleden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}u geleden`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d geleden`;
  return parseServerDate(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

function TaskComments({ taskId, currentUserId, isAdmin }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    api(`/api/tasks/${taskId}/comments`)
      .then(data => setComments(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [taskId]);

  async function handlePost() {
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      const comment = await api(`/api/tasks/${taskId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content: text.trim() }),
      });
      setComments(prev => [...prev, comment]);
      setText('');
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch {}
    setPosting(false);
  }

  async function handleDelete(commentId) {
    setDeletingId(commentId);
    try {
      await api(`/api/tasks/${taskId}/comments/${commentId}`, { method: 'DELETE' });
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {}
    setDeletingId(null);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handlePost();
  }

  const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const avatarColor = (name) => {
    const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444'];
    let h = 0; for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffff;
    return colors[h % colors.length];
  };

  return (
    <div style="margin-top:24px">
      <Card title={`💬 Reacties${comments.length > 0 ? ` (${comments.length})` : ''}`}>
        {loading && <div class="text-muted text-sm" style="padding:8px 0">Laden…</div>}

        {!loading && comments.length === 0 && (
          <div class="text-muted text-sm" style="padding:4px 0 12px">Nog geen reacties. Wees de eerste!</div>
        )}

        {/* Comment list */}
        {comments.length > 0 && (
          <div style="display:flex;flex-direction:column;gap:12px;margin-bottom:16px">
            {comments.map(c => {
              const canDelete = Number(c.user_id) === Number(currentUserId) || isAdmin;
              return (
                <div key={c.id} style="display:flex;gap:10px;align-items:flex-start">
                  {/* Avatar */}
                  <div style={`width:34px;height:34px;border-radius:50%;background:${avatarColor(c.author_name)};display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#fff;flex-shrink:0`}>
                    {initials(c.author_name)}
                  </div>

                  {/* Bubble */}
                  <div style="flex:1;min-width:0">
                    <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:0 10px 10px 10px;padding:8px 12px">
                      <div style="display:flex;align-items:baseline;gap:8px;margin-bottom:4px;flex-wrap:wrap">
                        <span style="font-size:13px;font-weight:600">{c.author_name}</span>
                        <span style="font-size:11px;color:var(--color-text-muted)">{timeAgo(c.created_at)}</span>
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(c.id)}
                            disabled={deletingId === c.id}
                            title="Verwijderen"
                            style="margin-left:auto;background:none;border:none;cursor:pointer;color:var(--color-text-muted);font-size:12px;padding:0;opacity:0.6;line-height:1"
                          >
                            🗑
                          </button>
                        )}
                      </div>
                      <div style="font-size:13px;line-height:1.5;white-space:pre-wrap;word-break:break-word">{c.content}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}

        {/* New comment input */}
        <div style="display:flex;gap:8px;align-items:flex-end">
          <textarea
            class="form-textarea"
            style="flex:1;resize:none;font-size:13px;min-height:60px"
            placeholder="Schrijf een reactie… (Ctrl+Enter om te versturen)"
            value={text}
            onInput={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={handlePost}
            disabled={posting || !text.trim()}
            style="flex-shrink:0;align-self:flex-end"
          >
            {posting ? '…' : 'Versturen'}
          </Button>
        </div>
        <AiAssist kind="task_comment" value={text} onUse={setText} />
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:4px">Ctrl+Enter om snel te versturen</div>
      </Card>
    </div>
  );
}

const TASK_STATUSES = ['open', 'in_progress', 'wacht_op_input', 'afgerond', 'geannuleerd'];

function SkeletonLine({ width = '100%', height = '16px', style = '' }) {
  return <div class="skeleton" style={`width:${width};height:${height};${style}`} />;
}

function TaskDetailSkeleton() {
  return (
    <div>
      <div class="page-header">
        <SkeletonLine width="80px" height="32px" />
      </div>
      <div class="grid-2 mt-4" style="align-items:start">
        <div class="card" style="padding:20px">
          <SkeletonLine width="60%" height="24px" style="margin-bottom:8px" />
          <SkeletonLine width="40%" height="14px" style="margin-bottom:24px" />
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} style="margin-bottom:16px">
              <SkeletonLine width="100px" height="12px" style="margin-bottom:6px" />
              <SkeletonLine height="36px" />
            </div>
          ))}
        </div>
        <div style="display:flex;flex-direction:column;gap:16px">
          <div class="card" style="padding:20px">
            <SkeletonLine width="80px" height="16px" style="margin-bottom:16px" />
            {[0, 1, 2, 3].map(i => <SkeletonLine key={i} height="32px" style="margin-bottom:8px" />)}
          </div>
          <div class="card" style="padding:20px">
            <SkeletonLine width="100px" height="16px" style="margin-bottom:16px" />
            <SkeletonLine height="80px" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TaskDetailView({ id }) {
  const auth = useAuth();
  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({ title: '', description: '', status: '', deadline: '', start_date: '', notes: '', owner_id: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const successTimer = useRef(null);

  const [delegateOpen, setDelegateOpen] = useState(false);
  const [delegateUserId, setDelegateUserId] = useState('');
  const [delegating, setDelegating] = useState(false);
  const [delegateError, setDelegateError] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Document upload
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Document preview
  const [previewDoc, setPreviewDoc] = useState(null);

  const currentUserId = Number(auth.userId);
  // Club admin = has settings.update permission (applicatiebeheerder) or platform admin
  const isAdmin = auth.isPlatformAdmin || auth.hasPermission('settings', 'update');
  // Owner = explicit owner_id or, for unowned tasks, the creator
  const isOwner = !!task && (
    Number(task.owner_id) === currentUserId ||
    (!task.owner_id && Number(task.created_by) === currentUserId)
  );
  const isProcessResponsible = !!task && Number(task.process_assigned_to) === currentUserId;

  // Field-level edit permissions
  const canEditOwner = isAdmin; // only club admin can reassign ownership
  const canEditDeadline = isAdmin || isOwner || isProcessResponsible;
  const canEditFields = isAdmin || isOwner; // title/desc/status/notes
  // Delegate: the owner or an admin, with task-edit rights (the route requires tasks:update)
  const canDelegate = (isOwner || isAdmin) && (auth.isPlatformAdmin || auth.hasPermission('tasks', 'update'));
  const canDelete = isOwner || isAdmin;
  const canUploadDoc = auth.hasPermission('documents', 'create');

  const fetchTask = () =>
    api(`/api/tasks/${id}`).then((data) => {
      setTask(data);
      setForm({
        title: data.title || '',
        description: data.description || '',
        status: data.status || '',
        deadline: data.deadline ? data.deadline.slice(0, 10) : '',
        start_date: data.start_date ? data.start_date.slice(0, 10) : '',
        notes: data.notes || '',
        owner_id: data.owner_id ? String(data.owner_id) : '',
      });
    });

  const fetchDocs = () =>
    api(`/api/documents?taskId=${id}`)
      .then(setDocs)
      .catch(() => setDocs([]));

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchTask(),
      api('/api/users/members').catch(() => []),
      api(`/api/documents?taskId=${id}`).catch(() => []),
    ])
      .then(([, usrs, taskDocs]) => {
        setUsers(Array.isArray(usrs) ? usrs : []);
        setDocs(Array.isArray(taskDocs) ? taskDocs : []);
      })
      .catch((err) => setError(translateError(err.code, err.detail)))
      .finally(() => setLoading(false));
  }, [id]);

  const handleFormChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    clearTimeout(successTimer.current);
    try {
      const updated = await api(`/api/tasks/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...form,
          ownerId: form.owner_id ? parseInt(form.owner_id) : null,
          deadline: form.deadline || null,
          startDate: form.start_date || null,
        }),
      });
      setTask(updated);
      setForm((prev) => ({ ...prev, owner_id: updated.owner_id ? String(updated.owner_id) : '' }));
      setSaveSuccess(true);
      successTimer.current = setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(translateError(err.code, err.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api(`/api/tasks/${id}`, { method: 'DELETE' });
      route('/tasks');
    } catch {
      setConfirmDelete(false);
      setDeleting(false);
    }
  };

  const openDelegate = () => {
    setDelegateError(null);
    setDelegateUserId('');
    setDelegateOpen(true);
  };

  const handleDelegate = async () => {
    if (!delegateUserId) return;
    setDelegating(true);
    setDelegateError(null);
    try {
      await api(`/api/tasks/${id}/delegate`, {
        method: 'POST',
        body: JSON.stringify({ userId: Number(delegateUserId) }),
      });
      setDelegateOpen(false);
      await fetchTask();
    } catch (err) {
      setDelegateError(translateError(err.code, err.detail));
    } finally {
      setDelegating(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('taskId', id);
    try {
      const res = await fetch(`/api/documents`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      await fetchDocs();
    } catch {
      setUploadError(t('document.uploadError'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatDate = (val) => val ? parseServerDate(val).toLocaleDateString() : '-';
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImage = (doc) => doc.file_type?.startsWith('image/');
  const isPdf = (doc) => doc.file_type === 'application/pdf';

  if (loading) return <TaskDetailSkeleton />;

  if (error) {
    return (
      <div>
        <Button variant="secondary" onClick={() => history.back()}>{t('common.back')}</Button>
        <div class="alert alert-error mt-4">{error}</div>
      </div>
    );
  }

  if (!task) return null;

  return (
    <div>
      <div class="page-header">
        <Button variant="secondary" size="sm" onClick={() => history.back()}>
          {t('common.back')}
        </Button>
        {canDelete && (
          <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
            {t('task.delete')}
          </Button>
        )}
      </div>

      <div class="grid-2 mt-4" style="align-items:start">
        {/* Main edit form */}
        <div>
          <Card>
            {/* Read-only / limited-edit notice */}
            {task.owner_id && !isOwner && !isAdmin && isProcessResponsible && (
              <div style="background:var(--color-info-light,#eff6ff);border:1px solid var(--color-info,#3b82f6);border-radius:8px;padding:10px 14px;margin-bottom:16px;font-size:13px;color:var(--color-text)">
                Je bent verantwoordelijke voor dit proces. Je kunt alleen de <strong>deadline</strong> aanpassen. De taak is toegewezen aan <strong>{task.owner_name}</strong>.
              </div>
            )}
            {task.owner_id && !isOwner && !isAdmin && !isProcessResponsible && (
              <div style="background:var(--color-warning-light,#fef9c3);border:1px solid var(--color-warning,#eab308);border-radius:8px;padding:10px 14px;margin-bottom:16px;font-size:13px;color:var(--color-text)">
                Deze taak is toegewezen aan <strong>{task.owner_name}</strong>. Je kunt hem bekijken maar niet bewerken.
              </div>
            )}

            <div class="flex-between mb-4">
              <div>
                <h2 style="font-size:20px;font-weight:700;margin:0 0 6px">{task.title}</h2>
                {task.process_id && (
                  <a
                    class="text-sm text-accent"
                    style="cursor:pointer"
                    onClick={() => route(`/processes/${task.process_id}`)}
                  >
                    {t('process.title')}: {task.process_title || `#${task.process_id}`}
                  </a>
                )}
                <ItemGoals type="task" id={task.id} />
              </div>
              <StatusBadge status={task.status} type="task" />
            </div>

            <div class="form-group">
              <label class="form-label">{t('task.taskTitle')}</label>
              <input
                class="form-input"
                type="text"
                value={form.title}
                onInput={(e) => handleFormChange('title', e.target.value)}
                disabled={!canEditFields}
              />
            </div>

            <div class="form-group">
              <label class="form-label">{t('task.description')}</label>
              <textarea
                class="form-textarea"
                rows={3}
                value={form.description}
                onInput={(e) => handleFormChange('description', e.target.value)}
                disabled={!canEditFields}
              />
              {canEditFields && <AiAssist kind="task_description" value={form.description} context={{ title: form.title, process: task.process_title || '' }} onUse={text => handleFormChange('description', text)} />}
            </div>

            <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
              <div class="form-group">
                <label class="form-label">
                  {t('task.owner')}
                  {!canEditOwner && task.owner_name && (
                    <span style="font-weight:400;color:var(--color-text-muted)"> — {task.owner_name}</span>
                  )}
                </label>
                {canEditOwner ? (
                  <>
                    <select
                      class="form-select"
                      value={form.owner_id}
                      onChange={(e) => handleFormChange('owner_id', e.target.value)}
                    >
                      <option value="">—</option>
                      {users.map((u) => (
                        <option key={u.id} value={String(u.id)}>{memberLabel(u)}</option>
                      ))}
                    </select>
                    <AwayNote users={users} userId={form.owner_id} />
                  </>
                ) : (
                  <div class="form-input" style="background:var(--color-bg-secondary,#f9fafb);color:var(--color-text-muted);cursor:default">
                    {task.owner_name || '—'}
                  </div>
                )}
              </div>

              <div class="form-group">
                <label class="form-label">{t('task.status')}</label>
                <select
                  class="form-select"
                  value={form.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  disabled={!canEditFields}
                >
                  {TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>{t(`task.statuses.${s}`)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
              <div class="form-group">
                <label class="form-label">Startdatum</label>
                <input
                  class="form-input"
                  type="date"
                  value={form.start_date}
                  onInput={(e) => handleFormChange('start_date', e.target.value)}
                  disabled={!canEditDeadline}
                />
              </div>
              <div class="form-group">
                <label class="form-label">{t('task.deadline')}</label>
                <input
                  class="form-input"
                  type="date"
                  value={form.deadline}
                  onInput={(e) => handleFormChange('deadline', e.target.value)}
                  disabled={!canEditDeadline}
                />
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">{t('task.notes')}</label>
              <textarea
                class="form-textarea"
                rows={3}
                value={form.notes}
                onInput={(e) => handleFormChange('notes', e.target.value)}
                disabled={!canEditFields}
              />
            </div>

            {saveError && <div class="alert alert-error mb-3">{saveError}</div>}
            {saveSuccess && <div class="alert alert-success mb-3">{t('club.saved')}</div>}

            <div class="flex gap-2 justify-end">
              {canDelegate && (
                <Button variant="secondary" onClick={openDelegate}>
                  {t('task.delegate')}
                </Button>
              )}
              {(canEditFields || canEditDeadline) && (
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? t('common.loading') : t('common.save')}
                </Button>
              )}
            </div>
          </Card>
        </div>

        {/* Right column: details + documents */}
        <div style="display:flex;flex-direction:column;gap:16px">
          <Card title={t('common.details')}>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">{t('task.createdBy')}</span>
                <span class="detail-value">{task.created_by_name || '-'}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{t('task.createdAt')}</span>
                <span class="detail-value">{formatDate(task.created_at)}</span>
              </div>
              {task.process_assigned_to_name && (
                <div class="detail-item">
                  <span class="detail-label">{t('process.assignedTo')}</span>
                  <span class="detail-value">{task.process_assigned_to_name}</span>
                </div>
              )}
              {task.delegated_from_name && (
                <div class="detail-item">
                  <span class="detail-label">{t('task.delegate')}</span>
                  <span class="detail-value">{task.delegated_from_name}</span>
                </div>
              )}
              {task.completed_at && (
                <div class="detail-item">
                  <span class="detail-label">{t('task.completedAt')}</span>
                  <span class="detail-value">{formatDate(task.completed_at)}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Documents */}
          <Card title={t('document.title')}>
            {docs.length === 0 && (
              <p class="text-muted text-sm" style="margin-bottom:12px">{t('document.noDocuments')}</p>
            )}
            {docs.length > 0 && (
              <div style="margin-bottom:12px;border:1px solid var(--color-border,#e5e7eb);border-radius:8px;overflow:hidden">
                {docs.map((doc, idx) => (
                  <div
                    key={doc.id}
                    style={`display:flex;align-items:center;gap:10px;padding:8px 12px;${idx > 0 ? 'border-top:1px solid var(--color-border,#e5e7eb)' : ''}`}
                  >
                    <div style="flex:1;min-width:0">
                      <div style="font-size:13px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{doc.file_name}</div>
                      <div class="text-muted" style="font-size:11px">{formatSize(doc.file_size)}</div>
                    </div>
                    <div class="flex gap-1">
                      {(isImage(doc) || isPdf(doc)) && (
                        <Button variant="secondary" size="sm" onClick={() => setPreviewDoc(doc)}>
                          {t('document.preview')}
                        </Button>
                      )}
                      <Button variant="secondary" size="sm" onClick={() => openFile(`/api/documents/${doc.id}/download`, doc.file_name)}>
                        {t('document.download')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {canUploadDoc && (
              <div>
                {uploadError && <div class="alert alert-error mb-2" style="font-size:13px">{uploadError}</div>}
                <input
                  ref={fileInputRef}
                  type="file"
                  style="display:none"
                  onChange={handleUpload}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? t('common.loading') : t('document.upload')}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Delegate modal */}
      <Modal isOpen={delegateOpen} title={t('task.delegateTask')} onClose={() => setDelegateOpen(false)}>
        <div class="form-group">
          <label class="form-label">{t('task.delegateTo')}</label>
          <select
            class="form-select"
            value={delegateUserId}
            onChange={(e) => setDelegateUserId(e.target.value)}
          >
            <option value="">{t('task.delegateTo')}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{memberLabel(u)}</option>
            ))}
          </select>
          <AwayNote users={users} userId={delegateUserId} />
        </div>
        {delegateError && <div class="alert alert-error mb-3">{delegateError}</div>}
        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setDelegateOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleDelegate} disabled={!delegateUserId || delegating}>
            {delegating ? t('common.loading') : t('common.confirm')}
          </Button>
        </div>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal isOpen={confirmDelete} title={t('task.delete')} onClose={() => setConfirmDelete(false)}>
        <p style="margin-bottom:20px">{t('task.confirmDelete')}</p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? t('common.loading') : t('common.delete')}
          </Button>
        </div>
      </Modal>

      {/* Collaborators section */}
      <TaskCollaborators
        taskId={parseInt(id)}
        canManage={isOwner || isAdmin}
        currentUserId={currentUserId}
        members={users}
      />

      {/* Comments section */}
      <TaskComments taskId={parseInt(id)} currentUserId={currentUserId} isAdmin={isAdmin} />

      {/* Document preview modal */}
      <Modal
        isOpen={!!previewDoc}
        title={previewDoc?.file_name || ''}
        onClose={() => setPreviewDoc(null)}
        style="max-width:800px"
      >
        {previewDoc && (
          <div style="text-align:center;max-height:70vh;overflow:auto">
            {isImage(previewDoc) ? (
              <img
                src={`/api/documents/${previewDoc.id}/preview`}
                alt={previewDoc.file_name}
                style="max-width:100%;max-height:65vh;object-fit:contain;border-radius:4px"
              />
            ) : isPdf(previewDoc) ? (
              <PdfFrame
                src={`/api/documents/${previewDoc.id}/preview`}
                title={previewDoc.file_name}
                style="width:100%;height:65vh;border:none;border-radius:4px"
              />
            ) : (
              <p class="text-muted">{t('document.noPreview')}</p>
            )}
          </div>
        )}
        <div class="flex justify-end mt-4">
          <Button variant="secondary" size="sm" onClick={() => openFile(`/api/documents/${previewDoc?.id}/download`, previewDoc?.file_name)}>
            {t('document.download')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
